import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { Badge } from '../components/UI';
import styles from './MemberDetail.module.css';
import {
  ArrowLeft, User, Briefcase, Globe, Star, MapPin,
  Phone, Mail, Calendar, Clock, Award, Shield, Activity,
  BookOpen, CheckCircle, AlertCircle, MinusCircle, FileText, Users
} from 'lucide-react';
import {
  FIELD_EPI_SKILLS, PROJECT_MGMT_SKILLS, ME_SKILLS,
  GHS_SKILLS, INCIDENT_SKILLS, LEADERSHIP_SKILLS, DEPLOYMENT_DURATION
} from '../data/formSchema';

const RATING_LABELS = ['', 'Basic', 'Limited', 'Proficient', 'Highly Proficient', 'Expert'];
const RATING_BG     = ['', '#f3f4f6', '#fef3c7', '#dbeafe', '#d1fae5', '#dcfce7'];
const RATING_COLOR  = ['', '#6b7280', '#92400e', '#1e40af', '#065f46', '#14532d'];

export default function MemberDetail() {
  const { id }       = useParams();
  const navigate     = useNavigate();
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const snap = await getDoc(doc(db, 'alumni', id));
      if (snap.exists()) setMember({ id: snap.id, ...snap.data() });
      setLoading(false);
    })();
  }, [id]);

  if (loading) return (
    <div className={styles.loadingPage}>
      <div className={styles.spinner} />
      <p>Loading member profile…</p>
    </div>
  );

  if (!member) return (
    <div className={styles.loadingPage}>
      <AlertCircle size={36} style={{ color: 'var(--red-400)' }} />
      <p>Member not found.</p>
      <Link to="/admin" className={styles.backLink}>← Back to Dashboard</Link>
    </div>
  );

  const displayTitle  = member.title === 'Other' ? member.titleOther : member.title;
  const fullName      = [displayTitle, member.firstName, member.surname].filter(Boolean).join(' ');
  const initials      = `${member.firstName?.[0] || ''}${member.surname?.[0] || ''}`;
  const submittedDate = member.submittedAt?.toDate?.()?.toLocaleDateString('en-GB', {
    day: 'numeric', month: 'long', year: 'numeric',
  }) || '—';

  // All language keys to display (excluding legacy 'swahili' if present, keeping kiswahili)
  const allLanguageKeys = ['english', 'arabic', 'french', 'kiswahili', 'portuguese', 'chinese', 'russian', 'spanish'];

  return (
    <div className={styles.page}>

      {/* ── Top Nav Bar ── */}
      <div className={styles.topNav}>
        <button className={styles.backBtn} onClick={() => navigate('/admin')}>
          <ArrowLeft size={15} /> Back to Roster
        </button>
        <div className={styles.topNavRight}>
          <Badge color={member.consent ? 'green' : 'red'}>
            {member.consent ? '✓ Consent Given' : '✗ No Consent'}
          </Badge>
          <span className={styles.submittedOn}>Submitted {submittedDate}</span>
        </div>
      </div>

      {/* ── Hero Header ── */}
      <div className={styles.hero}>
        <div className={styles.heroInner}>
          <div className={styles.heroLeft}>
            <div className={styles.bigAvatar}>{initials}</div>
            <div>
              <h1 className={styles.heroName}>{fullName}</h1>
              <p className={styles.heroRole}>
                {member.currentRole || member.currentEmployer || 'Field Epidemiologist'}
              </p>
              <div className={styles.heroTags}>
                {member.currentCountry && (
                  <span className={styles.heroTag}><MapPin size={12} /> {member.currentCountry}</span>
                )}
                {member.preferredEmail && (
                  <span className={styles.heroTag}><Mail size={12} /> {member.preferredEmail}</span>
                )}
                {member.primaryPhone && (
                  <span className={styles.heroTag}><Phone size={12} /> {member.primaryPhone}</span>
                )}
              </div>
            </div>
          </div>
          <div className={styles.heroKpis}>
            <Kpi value={member.yearsExperience || '—'} label="Years Exp." />
            <Kpi value={member.graduationYear || '—'} label="FETP Grad." />
            <Kpi value={member.highestQualification || '—'} label="Qualification" small />
            <Kpi
              label="Intl. Available"
              value={
                member.internationalDeployment === 'Yes' ? '✓ Yes' :
                member.internationalDeployment === 'No'  ? '✗ No'  :
                member.internationalDeployment || '—'
              }
              highlight={member.internationalDeployment === 'Yes' ? 'green' : member.internationalDeployment === 'No' ? 'red' : ''}
            />
            <Kpi
              value={member.formDurationDisplay || '—'}
              label="Time to Complete"
              small
              highlight="clock"
            />
          </div>
        </div>
      </div>

      {/* ── Body ── */}
      <div className={styles.body}>

        {/* ══ SECTION A: PERSONAL & CONTACT ══ */}
        <Section letter="A" title="Personal & Contact Information">
          <div className={styles.twoCol}>
            <InfoCard title="Basic Details" icon={<User size={14} />}>
              {displayTitle && <Row label="Title"          value={displayTitle} />}
              <Row label="First Name"    value={member.firstName} />
              <Row label="Surname"       value={member.surname} />
              <Row label="Nationality"   value={member.nationality} />
              <Row label="Country of Residence" value={member.currentCountry} />
            </InfoCard>

            <InfoCard title="Contact Details" icon={<Phone size={14} />}>
              <Row label="Preferred Email"    value={member.preferredEmail} mono />
              <Row label="Alternative Email"  value={member.alternativeEmail} mono />
              <Row label="Primary Phone"      value={member.primaryPhone} />
              <Row label="Secondary Phone"    value={member.secondaryPhone} />
            </InfoCard>
          </div>

          <InfoCard title="Languages Spoken" icon={<Globe size={14} />} fullWidth>
            <div className={styles.langGrid}>
              {allLanguageKeys.map(lang => {
                const level = member.languages?.[lang];
                if (!level || level === 'None') return null;
                return (
                  <div key={lang} className={styles.langChip}>
                    <span className={styles.langName}>{lang.charAt(0).toUpperCase() + lang.slice(1)}</span>
                    <span className={styles.langLevel}>{level}</span>
                  </div>
                );
              })}
              {/* Handle legacy 'swahili' key from old submissions */}
              {member.languages?.swahili && member.languages.swahili !== 'None' && !member.languages?.kiswahili && (
                <div className={styles.langChip}>
                  <span className={styles.langName}>Kiswahili</span>
                  <span className={styles.langLevel}>{member.languages.swahili}</span>
                </div>
              )}
              {member.languages?.otherLanguage && member.languages?.other && (
                <div className={styles.langChip}>
                  <span className={styles.langName}>{member.languages.otherLanguage}</span>
                  <span className={styles.langLevel}>{member.languages.other}</span>
                </div>
              )}
            </div>
          </InfoCard>
        </Section>

        {/* ══ SECTION B: PROFESSIONAL ══ */}
        <Section letter="B" title="Professional Background & Current Placement">
          <div className={styles.twoCol}>
            <InfoCard title="Education & Experience" icon={<Award size={14} />}>
              <Row label="Highest Qualification"       value={member.highestQualification} />
              <Row label="Year of FETP Graduation"     value={member.graduationYear} />
              <Row label="Years of Experience"         value={member.yearsExperience ? `${member.yearsExperience} years` : undefined} />
              {member.formDurationDisplay && (
                <Row label="Time Taken to Complete Form" value={member.formDurationDisplay} />
              )}
            </InfoCard>

            <InfoCard title="Current Employment" icon={<Briefcase size={14} />}>
              <Row label="Employer / Organisation"     value={member.currentEmployer} />
              <Row label="Sector"                      value={member.sector} />
              {member.sectorOther && <Row label="Sector (Other)" value={member.sectorOther} />}
            </InfoCard>
          </div>

          {member.currentRole && (
            <InfoCard title="Current Role Description" icon={<FileText size={14} />} fullWidth>
              <p className={styles.prose}>{member.currentRole}</p>
            </InfoCard>
          )}
        </Section>

        {/* ══ SECTION C: AVAILABILITY ══ */}
        <Section letter="C" title="Availability for Deployment">
          <div className={styles.twoCol}>
            <InfoCard title="Deployment Availability" icon={<Clock size={14} />}>
              <Row label="Local Deployment"         value={member.localDeployment} />
              <Row label="International Deployment" value={member.internationalDeployment} />
              <Row label="Notice Period Required"   value={member.noticePeriod} />
              {member.noticePeriodOther && (
                <Row label="Notice (specified)" value={member.noticePeriodOther} />
              )}
            </InfoCard>

            <InfoCard title="Preferred Duration" icon={<Calendar size={14} />}>
              {DEPLOYMENT_DURATION.map(({ key, label }) => (
                <div key={key} className={styles.checkRow}>
                  <span className={`${styles.checkIcon} ${member.deploymentDuration?.[key] ? styles.checkYes : styles.checkNo}`}>
                    {member.deploymentDuration?.[key] ? '✓' : '○'}
                  </span>
                  <span className={member.deploymentDuration?.[key] ? styles.checkLabelYes : styles.checkLabelNo}>
                    {label}
                  </span>
                </div>
              ))}
            </InfoCard>
          </div>

          {member.deploymentRestrictions && (
            <InfoCard title="Deployment Restrictions / Notes" icon={<AlertCircle size={14} />} fullWidth>
              <p className={styles.prose}>{member.deploymentRestrictions}</p>
            </InfoCard>
          )}
        </Section>

        {/* ══ SECTION D: SKILLS ══ */}
        <Section letter="D" title="Key Skills & Expertise (Self-Assessment)">
          <div className={styles.ratingKey}>
            {[1,2,3,4,5].map(n => (
              <div key={n} className={styles.ratingKeyItem}>
                <div className={styles.ratingKeyDot} style={{ background: RATING_BG[n], color: RATING_COLOR[n] }}>{n}</div>
                <span>{RATING_LABELS[n]}</span>
              </div>
            ))}
          </div>

          <SkillBlock title="1. Field Epidemiology Core Skills"        skills={FIELD_EPI_SKILLS}      data={member.fieldEpiSkills}      otherSkills={member.fieldEpiOtherSkills} />
          {member.otherSoftwareName && (
            <div className={styles.infoNote}>Other analysis software: <strong>{member.otherSoftwareName}</strong></div>
          )}
          <SkillBlock title="2. Project Management Skills"             skills={PROJECT_MGMT_SKILLS}   data={member.projectMgmtSkills}   otherSkills={member.projectMgmtOtherSkills} />
          <SkillBlock title="3. Monitoring & Evaluation (M&E) Skills"  skills={ME_SKILLS}             data={member.meSkills}            otherSkills={member.meOtherSkills} />
          <SkillBlock title="4. Global Health Security (GHS) Skills"   skills={GHS_SKILLS}            data={member.ghsSkills}           otherSkills={member.ghsOtherSkills} />
          <SkillBlock title="5. Incident Management & Outbreak Response" skills={INCIDENT_SKILLS}     data={member.incidentSkills}      otherSkills={member.incidentOtherSkills} />
          <SkillBlock title="6. Leadership & Training Skills"          skills={LEADERSHIP_SKILLS}     data={member.leadershipSkills}    otherSkills={member.leadershipOtherSkills} />
        </Section>

        {/* ══ SECTION E: SPECIFIC EXPERTISE ══ */}
        <Section letter="E" title="Specific Expertise & Experience">
          {member.diseasesExperience && (
            <InfoCard title="Diseases/Pathogens You Have Specific Expertise & Experience With" icon={<Shield size={14} />} fullWidth>
              <p className={styles.prose}>{member.diseasesExperience}</p>
            </InfoCard>
          )}

          <div className={styles.twoCol}>
            {member.workRegions?.some(Boolean) && (
              <InfoCard title="Countries of Work Experience" icon={<MapPin size={14} />}>
                {member.workRegions.filter(Boolean).map((r, i) => (
                  <Row key={i} label={`Country ${i + 1}`} value={r} />
                ))}
              </InfoCard>
            )}

            {(member.workContinents || []).length > 0 && (
              <InfoCard title="Continents of Work Experience" icon={<Globe size={14} />}>
                <div className={styles.continentTags}>
                  {member.workContinents.map(c => (
                    <span key={c} className={styles.continentTag}>{c}</span>
                  ))}
                </div>
              </InfoCard>
            )}

            <InfoCard title="Humanitarian & High-Risk" icon={<Activity size={14} />}>
              <Row label="Humanitarian / Fragile Settings" value={member.humanitarianExperience} />
              {member.humanitarianContext && (
                <Row label="Context" value={member.humanitarianContext} />
              )}
              <Row label="Willing to Work in High-Risk Environments" value={member.highRiskWillingness} />
            </InfoCard>
          </div>

          {member.certifications && (
            <InfoCard title="Technical Certifications & Advanced Training" icon={<Award size={14} />} fullWidth>
              <p className={styles.prose}>{member.certifications}</p>
            </InfoCard>
          )}

          {member.additionalSkills && (
            <InfoCard title="Additional Skills & Areas of Expertise" icon={<Star size={14} />} fullWidth>
              <p className={styles.prose}>{member.additionalSkills}</p>
            </InfoCard>
          )}
        </Section>

        {/* ══ SECTION F: REFERENCES ══ */}
        <Section letter="F" title="Professional References">
          <p className={styles.refOptionalNote}>
            Optional, but highly recommended for rapid deployment consideration
          </p>
          {!(member.references || []).length && !member.ref1 && !member.ref2 && !member.ref3 ? (
            <div className={styles.emptyState}>
              <BookOpen size={28} style={{ color: 'var(--gray-300)' }} />
              <p>No references provided.</p>
            </div>
          ) : (
            <div className={styles.twoCol}>
              {/* New structured references */}
              {(member.references || []).map((ref, i) =>
                ref ? (
                  <InfoCard key={`ref-new-${i}`} title={`Reference ${i + 1}`} icon={<Users size={14} />}>
                    {ref.name && <Row label="Name" value={ref.name} />}
                    {ref.title && <Row label="Title" value={ref.title} />}
                    {ref.organization && <Row label="Organization" value={ref.organization} />}
                    {ref.email && <Row label="Email" value={ref.email} mono />}
                    {ref.phone && <Row label="Phone" value={ref.phone} />}
                  </InfoCard>
                ) : null
              )}
              {/* Legacy plain-text references (backward compatibility) */}
              {!member.references?.length && [member.ref1, member.ref2, member.ref3].map((ref, i) =>
                ref ? (
                  <InfoCard key={`ref-legacy-${i}`} title={`Reference ${i + 1}`} icon={<Users size={14} />}>
                    <p className={styles.prose}>{ref}</p>
                  </InfoCard>
                ) : null
              )}
            </div>
          )}
        </Section>

      </div>
    </div>
  );
}

/* ────────────────────────────────────────────
   Sub-components
──────────────────────────────────────────── */

function Section({ letter, title, children }) {
  return (
    <section className={styles.section}>
      <div className={styles.sectionHeader}>
        <div className={styles.sectionBadge}>{letter}</div>
        <h2 className={styles.sectionTitle}>{title}</h2>
      </div>
      <div className={styles.sectionBody}>{children}</div>
    </section>
  );
}

function InfoCard({ title, icon, children, fullWidth }) {
  return (
    <div className={`${styles.card} ${fullWidth ? styles.fullWidth : ''}`}>
      <div className={styles.cardHead}>{icon} {title}</div>
      <div className={styles.cardBody}>{children}</div>
    </div>
  );
}

function Row({ label, value, mono }) {
  if (!value && value !== 0) return (
    <div className={styles.row}>
      <span className={styles.rowLabel}>{label}</span>
      <span className={styles.rowEmpty}>—</span>
    </div>
  );
  return (
    <div className={styles.row}>
      <span className={styles.rowLabel}>{label}</span>
      <span className={`${styles.rowValue} ${mono ? styles.mono : ''}`}>{value}</span>
    </div>
  );
}

function Kpi({ value, label, small, highlight }) {
  const colors = {
  green: 'var(--white)',
  red:   'var(--white)',
  clock: 'var(--white)',
  '':    'var(--white)',
};
  return (
    <div className={styles.kpi}>
      <span
        className={`${styles.kpiValue} ${small ? styles.kpiSmall : ''}`}
        style={{ color: highlight ? colors[highlight] : undefined }}
      >
        {value}
      </span>
      <span className={styles.kpiLabel}>{label}</span>
    </div>
  );
}

function SkillBlock({ title, skills, data = {}, otherSkills }) {
  return (
    <div className={styles.skillBlock}>
      <h3 className={styles.skillBlockTitle}>{title}</h3>
      <div className={styles.skillRows}>
        {skills.map(({ key, label }) => {
          const val = data[key] || 0;
          return (
            <div key={key} className={styles.skillRow}>
              <span className={styles.skillLabel}>{label}</span>
              <div className={styles.skillRight}>
                <div className={styles.skillTrack}>
                  <div
                    className={styles.skillFill}
                    style={{
                      width: `${(val / 5) * 100}%`,
                      background: val
                        ? 'linear-gradient(90deg, var(--teal-500), var(--teal-300))'
                        : 'transparent',
                    }}
                  />
                </div>
                {val > 0 ? (
                  <span
                    className={styles.skillPill}
                    style={{ background: RATING_BG[val], color: RATING_COLOR[val] }}
                  >
                    {val} — {RATING_LABELS[val]}
                  </span>
                ) : (
                  <span className={styles.skillPillEmpty}>Not rated</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {otherSkills && (
        <div className={styles.otherSkillsNote}>
          <span className={styles.otherSkillsNoteLabel}>Other skills:</span>
          <span className={styles.otherSkillsNoteText}>{otherSkills}</span>
        </div>
      )}
    </div>
  );
}